import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  MenuItem,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useMemo } from "react";
import { type Resolver, FormProvider, useForm } from "react-hook-form";

import {
  REASON_CODES,
  changeTenantPricingAssignmentSchema,
  createSubaccountPricingAssignmentsSchema,
} from "@stw/yup-validators";

import {
  ChangeTenantPricingAssignmentMutation,
  ChangeTenantPricingAssignmentMutationVariables,
  CreateSubaccountPricingAssignmentsMutation,
  CreateSubaccountPricingAssignmentsMutationVariables,
  GetActivePricingBundlesQuery,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  SubaccountDropdown,
  SubaccountDropdownItem,
} from "~frontend/components/forms/ClientDropdowns";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfDatePicker,
  RhfErrorBanner,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import {
  CHANGE_TENANT_PRICING_ASSIGNMENT,
  CREATE_SUBACCOUNT_PRICING_ASSIGNMENTS,
  GET_TENANT_PRICING,
} from "~frontend/graphql/pricingBundle";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { formatMillicentsAsDollars } from "../pricingV1/priceFormatting";
import { reasonCodeLabel, toTimestamp } from "./helpers";

type Bundle = GetActivePricingBundlesQuery["getActivePricingBundles"][0];

// Form values use DateTime | null for date pickers; the shared Zod schema
// transforms them to Date during validation.
type FormValues = {
  subaccountIds: string[];
  primaryBundleId: string;
  primaryReasonCode: string;
  primaryNotes: string;
  effectiveDate: DateTime | null;
  expirationDate: DateTime | null;
  afterBundleId: string;
  afterReasonCode: string;
  afterNotes: string;
};

type TenantModeProps = {
  open: boolean;
  onClose: () => void;
  bundles: Bundle[];
  mode?: "tenant";
  currentBundleId: string;
};

type SubaccountModeProps = {
  open: boolean;
  onClose: () => void;
  bundles: Bundle[];
  mode: "subaccount";
  subaccounts: SubaccountDropdownItem[];
};

export type ChangePricingDialogProps = TenantModeProps | SubaccountModeProps;

const DEFAULT_FORM_VALUES: FormValues = {
  subaccountIds: [],
  primaryBundleId: "",
  primaryReasonCode: "",
  primaryNotes: "",
  effectiveDate: null,
  expirationDate: null,
  afterBundleId: "",
  afterReasonCode: "",
  afterNotes: "",
};

function BundlePricePreview({ bundle }: { bundle: Bundle }): ReactElement {
  return (
    <FlexBox direction="column">
      <FlexBox
        alignItems="center"
        sx={{ px: 2, height: "2.5em", backgroundColor: palette.lightGray }}
      >
        <Typography variant="body2Bold">{bundle.name} Pricing</Typography>
      </FlexBox>
      <TwoColumnDisplay
        allRowSx={{
          backgroundColor: palette.lightestGray,
          px: 2,
        }}
        labelWidth="200px"
        labelVariant="body2Bold"
        contentVariant="body2"
        rows={bundle.prices.map(price => ({
          key: price.id,
          label: price.sku.name,
          content: `$${formatMillicentsAsDollars(price.priceInMillicents)}`,
        }))}
        showTopBorder={false}
        showBottomBorder={false}
      />
    </FlexBox>
  );
}

export function ChangePricingDialog(
  props: ChangePricingDialogProps
): ReactElement {
  const { open, onClose, bundles } = props;
  const isSubaccountMode = props.mode === "subaccount";
  const subaccountsProp = isSubaccountMode ? props.subaccounts : undefined;
  const subaccounts = useMemo<SubaccountDropdownItem[]>(
    () => subaccountsProp ?? [],
    [subaccountsProp]
  );
  const currentBundleId = isSubaccountMode ? undefined : props.currentBundleId;

  const resolver = isSubaccountMode
    ? (zodResolver(
        createSubaccountPricingAssignmentsSchema
      ) as unknown as Resolver<FormValues>)
    : (zodResolver(
        changeTenantPricingAssignmentSchema
      ) as unknown as Resolver<FormValues>);

  const methods = useForm<FormValues>({
    resolver,
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onSubmit",
  });

  const { control, handleSubmit, watch, setError, formState, reset, setValue } =
    methods;

  const [changePricing] = useMutation<
    ChangeTenantPricingAssignmentMutation,
    ChangeTenantPricingAssignmentMutationVariables
  >(CHANGE_TENANT_PRICING_ASSIGNMENT, {
    refetchQueries: [GET_TENANT_PRICING],
  });

  const [createSubaccountPricing] = useMutation<
    CreateSubaccountPricingAssignmentsMutation,
    CreateSubaccountPricingAssignmentsMutationVariables
  >(CREATE_SUBACCOUNT_PRICING_ASSIGNMENTS, {
    refetchQueries: [GET_TENANT_PRICING],
  });

  const formatErrorMessage = useErrorMessageFormatter();

  const selectedSubaccountIds = watch("subaccountIds");
  const primaryBundleId = watch("primaryBundleId");
  const afterBundleId = watch("afterBundleId");
  const expirationDate = watch("expirationDate");
  const primaryReasonCode = watch("primaryReasonCode");
  const afterReasonCode = watch("afterReasonCode");

  // In subaccount mode, WeText and non-WeText subaccounts cannot be mixed in
  // one assignment — pick the lock from the first selected subaccount and
  // disable the opposite type. Reset when selection clears.
  const weTextLock: boolean | null = useMemo(() => {
    if (!isSubaccountMode || selectedSubaccountIds.length === 0) {
      return null;
    }
    const first = subaccounts.find(s => s.id === selectedSubaccountIds[0]);
    return first?.isWeText ?? null;
  }, [isSubaccountMode, selectedSubaccountIds, subaccounts]);

  const disabledSubaccountIds = useMemo(() => {
    if (!isSubaccountMode || weTextLock === null) {
      return undefined;
    }
    return subaccounts
      .filter(s => (s.isWeText ?? false) !== weTextLock)
      .map(s => s.id);
  }, [isSubaccountMode, subaccounts, weTextLock]);

  const visibleBundles = useMemo(() => {
    if (!isSubaccountMode || weTextLock === null) {
      return bundles;
    }
    return bundles.filter(b => b.isWeText === weTextLock);
  }, [isSubaccountMode, bundles, weTextLock]);

  // If the selected bundle is no longer visible after a WeText lock change,
  // clear it so the form doesn't submit with a hidden-but-stale value.
  useEffect(() => {
    if (
      primaryBundleId &&
      !visibleBundles.some(b => b.id === primaryBundleId)
    ) {
      setValue("primaryBundleId", "");
    }
    if (afterBundleId && !visibleBundles.some(b => b.id === afterBundleId)) {
      setValue("afterBundleId", "");
    }
  }, [visibleBundles, primaryBundleId, afterBundleId, setValue]);

  const selectedPrimaryBundle = bundles.find(b => b.id === primaryBundleId);
  const selectedAfterBundle = bundles.find(b => b.id === afterBundleId);
  const hasExpiration = expirationDate != null;

  function clearAfterFields() {
    setValue("afterBundleId", "");
    setValue("afterReasonCode", "");
    setValue("afterNotes", "");
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function onSubmit(values: FormValues) {
    // Zod transforms DateTime → Date at runtime; cast here to reflect that.
    const effectiveDate = values.effectiveDate as unknown as Date;
    const expirationDate = values.expirationDate as unknown as Date | null;
    const sharedInput = {
      primaryBundleId: values.primaryBundleId,
      primaryReasonCode: values.primaryReasonCode,
      primaryNotes: values.primaryNotes || null,
      effectiveDate: toTimestamp(effectiveDate),
      expirationDate: expirationDate ? toTimestamp(expirationDate) : null,
      afterBundleId: values.afterBundleId || null,
      afterReasonCode: values.afterReasonCode || null,
      afterNotes: values.afterNotes || null,
    };

    const { errors } = isSubaccountMode
      ? await createSubaccountPricing({
          variables: {
            input: {
              ...sharedInput,
              subaccountIds: values.subaccountIds,
            },
          },
        })
      : await changePricing({ variables: { input: sharedInput } });

    if (errors) {
      setError("root", { message: formatErrorMessage(errors) });
    } else {
      handleClose();
    }
  }

  const title = isSubaccountMode
    ? "Add Subaccount Specific Pricing"
    : "Change Pricing";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h1" sx={{ pt: 3, pb: 1 }}>
        {title}
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 0 }}
          >
            <FlexBox flexDirection="column" gap={0}>
              <RhfErrorBanner control={control} />

              {isSubaccountMode && (
                <Box sx={{ pb: 1 }}>
                  <SubaccountDropdown
                    multiple
                    subaccounts={subaccounts}
                    disabledSubaccountIds={disabledSubaccountIds}
                    value={selectedSubaccountIds}
                    onChange={(newIds: string[]) => {
                      setValue("subaccountIds", newIds, {
                        shouldValidate: formState.isSubmitted,
                      });
                    }}
                    label="Subaccounts"
                    placeholder="Search subaccounts..."
                  />
                  {formState.errors.subaccountIds?.message ? (
                    <FormHelperText error>
                      {formState.errors.subaccountIds.message}
                    </FormHelperText>
                  ) : null}
                </Box>
              )}

              <RhfSelect
                control={control}
                name="primaryBundleId"
                label="Pricing Option"
                fullWidth
                onChange={(value: unknown) => {
                  if (value === afterBundleId) {
                    clearAfterFields();
                  }
                }}
              >
                {visibleBundles
                  .filter(bundle => bundle.id !== currentBundleId)
                  .map(bundle => (
                    <MenuItem key={bundle.id} value={bundle.id}>
                      {bundle.name}
                    </MenuItem>
                  ))}
              </RhfSelect>

              {selectedPrimaryBundle && (
                <Box sx={{ pb: 2 }}>
                  <BundlePricePreview bundle={selectedPrimaryBundle} />
                </Box>
              )}

              <RhfSelect
                control={control}
                name="primaryReasonCode"
                label="Change Reason"
                fullWidth
              >
                {REASON_CODES.map(code => (
                  <MenuItem key={code} value={code}>
                    {reasonCodeLabel(code)}
                  </MenuItem>
                ))}
              </RhfSelect>

              <RhfTextField
                control={control}
                name="primaryNotes"
                label="Notes"
                fullWidth
                multiline
                rows={1}
                optional={primaryReasonCode !== "OTHER"}
              />

              <FlexBox alignItems="flex-start" gap={2}>
                <RhfDatePicker
                  control={control}
                  name="effectiveDate"
                  label="Effective Date"
                  fullWidth
                  timezone="utc"
                  sx={{ maxHeight: "40px" }}
                />
                <RhfDatePicker
                  control={control}
                  name="expirationDate"
                  label="Expiration Date"
                  fullWidth
                  optional
                  onChange={(value: unknown) => {
                    if (value == null) {
                      clearAfterFields();
                    }
                  }}
                  timezone="utc"
                  sx={{ maxHeight: "40px" }}
                />
              </FlexBox>
            </FlexBox>

            {hasExpiration && (
              <FlexBox flexDirection="column" gap={0}>
                <RhfSelect
                  control={control}
                  name="afterBundleId"
                  label={
                    isSubaccountMode
                      ? "Pricing Option to Apply After Expiration Date (optional)"
                      : "Pricing Option to Apply After Expiration Date"
                  }
                  fullWidth
                >
                  {isSubaccountMode && (
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  )}
                  {visibleBundles
                    .filter(bundle => bundle.id !== primaryBundleId)
                    .map(bundle => (
                      <MenuItem key={bundle.id} value={bundle.id}>
                        {bundle.name}
                      </MenuItem>
                    ))}
                </RhfSelect>

                {selectedAfterBundle &&
                  selectedAfterBundle.id !== primaryBundleId && (
                    <Box sx={{ pb: 2 }}>
                      <BundlePricePreview bundle={selectedAfterBundle} />
                    </Box>
                  )}

                {(!isSubaccountMode || afterBundleId) && (
                  <>
                    <RhfSelect
                      control={control}
                      name="afterReasonCode"
                      label="Change Reason"
                      fullWidth
                    >
                      {REASON_CODES.map(code => (
                        <MenuItem key={code} value={code}>
                          {reasonCodeLabel(code)}
                        </MenuItem>
                      ))}
                    </RhfSelect>

                    <RhfTextField
                      control={control}
                      name="afterNotes"
                      label="Notes"
                      fullWidth
                      multiline
                      rows={1}
                      optional={afterReasonCode !== "OTHER"}
                    />
                  </>
                )}
              </FlexBox>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={formState.isSubmitting}
              onClick={handleClose}
              color="secondary"
            >
              Cancel
            </Button>
            <SubmitButton
              isSubmitting={formState.isSubmitting}
              submitText="Save"
              loadingText="Saving..."
            />
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
